/**
 * consoleGuard — Self-XSS deterrence for QuickRad (medical software).
 *
 * WHAT THIS IS:
 *   A prominent warning shown in the browser DevTools console, plus light
 *   deterrents against casually opening DevTools. This is the same approach
 *   used by Facebook, Google, etc. to defend against "Self-XSS" social
 *   engineering — where a victim is tricked into pasting attacker-supplied
 *   code into their own console.

 */

const STYLE_TITLE =
  "color:#c0392b;font-size:36px;font-weight:800;text-shadow:1px 1px 2px rgba(0,0,0,0.2);";
const STYLE_LEAD =
  "font-size:17px;font-weight:600;line-height:1.5;";
const STYLE_BODY = "color:#475569;font-size:13px;line-height:1.6;";
const STYLE_ALERT =
  "color:#ffffff;background:#c0392b;font-size:13px;font-weight:700;padding:4px 8px;border-radius:4px;";

function printWarning() {
  if (typeof console === "undefined" || typeof console.log !== "function") return;
  try {
    // eslint-disable-next-line no-console
    // console.log("%cStop!", STYLE_TITLE);
    // eslint-disable-next-line no-console
    
    console.log(
      "%cDO NOT SHARE YOUR CREDENTIALS. ",
      STYLE_LEAD
    );

    // eslint-disable-next-line no-console

    // console.log(
    //   "%cIf someone told you to copy and paste something here to enable a feature, " +
    //     "unlock access, or “fix” the application, it is a scam (Self-XSS). " +
    //     "Running it could expose protected patient data and compromise this account.",
    //   STYLE_BODY
    // );

    // eslint-disable-next-line no-console
    console.log(
      "%c⚠ QuickRad is HIPAA-regulated medical software. Do NOT paste or run any " +
        "script here, and do not make forceful changes. Activities may be logged.",
      STYLE_ALERT
    );
  } catch (e) {
    /* console may be locked down in some embeds — ignore */
  }
}

// Ensures the Self-XSS warning is printed at most once per page load.
let warned = false;

/**
 * Initialise the console guard.
 *
 * @param {Object}  [options]
 * @param {boolean} [options.deterShortcuts=true]    Block F12 / Ctrl+Shift+I/J/C / Ctrl+U (deterrent only).
 * @param {boolean} [options.disableContextMenu=true] Suppress right-click menu (deterrent only).
 *        NOTE: this also disables right-click "copy/paste" inside the app — turn it
 *        off here if it interferes with a clinical copy/paste workflow.
 */
export function initConsoleGuard(options = {}) {
  if (typeof window === "undefined") return;

  const {
    deterShortcuts = true,
    disableContextMenu = true,
  } = options;

  // 1) Self-XSS warning — printed exactly once per page load. It stays in the
  //    console buffer, so it appears a single time whenever the inspect panel
  //    is opened (no interval / no re-print on focus).
  if (!warned) {
    warned = true;
    printWarning();
  }

  // 2) Deterrents (bypassable — NOT security).
  if (disableContextMenu) {
    window.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  if (deterShortcuts) {
    window.addEventListener("keydown", (e) => {
      const key = (e.key || "").toLowerCase();
      const isDevToolsCombo =
        e.key === "F12" ||
        (e.ctrlKey && e.shiftKey && (key === "i" || key === "j" || key === "c")) ||
        (e.metaKey && e.altKey && (key === "i" || key === "j" || key === "c")) || // macOS
        (e.ctrlKey && key === "u"); // view-source
      if (isDevToolsCombo) {
        e.preventDefault();
        e.stopPropagation();
        printWarning();
      }
    });
  }
}

export default initConsoleGuard;
